/*******************************************************************************
 * Copyright     : Copyright (c)  2020 Honeywell Inc. All rights reserved.
 *                 This work contains trade secrets and confidential materials
 *                 of Honeywell Inc., and its use or disclosure in whole or in
 *                 part without express written permission of Honeywell Inc.
 *                 is prohibited.
 *
 * Name          : AuthProvider.tsx
 *
 * Project Title : RDR7000
 *
 * Description   : AuthProvider component
 *
 *
 * Company       : Honeywell Technology Solutions , Hyderabad
 *
 * @Author       : Amol Jagtap (H409453)
 *
 * @Date         : July 31, 2020
 *
 * @Version      : Version 1.0
 *
 * By            : Amol Jagtap (H409453)
 * On            : July 31, 2020
 * Description   : Added AuthProvider context for login and logout function
 *
 *
 ******************************************************************************/

import React, { FC, createContext, useContext, useState } from 'react';
import { Search } from 'history';
import { useDispatch, useSelector } from 'react-redux';
import { IGetAuthTokenRequestArgs, EnumTokenType, ITokenState } from 'models/auth';
import { appConfig, generateCodeVerifier, getAuthCodeChallenge, getLoginURL, getLogoutURL } from 'shared/utils';
import { fetchToken, selectIsAuthenticated } from 'stores/AuthStore';

interface IAuthContextProps {
  isAuthenticated: boolean;
  login: (location: { search: Search }) => void;
  logout: (authtoken: ITokenState | null) => void;
}

const AuthContext = createContext<Partial<IAuthContextProps>>({});

export const AuthProvider: FC = ({ children }) => {
  const dispatch = useDispatch();
  const isAuthenticated = useSelector(selectIsAuthenticated);
  const [codeVerifier, setCodeVerifier] = useState<string>(() => sessionStorage.getItem('codeVerifier') || '');

  const login = (location: { search: Search }) => {
    const query = new URLSearchParams(location.search);
    const code: string | null = query.get('code');

    if (!code) {
      const verifier = generateCodeVerifier();
      setCodeVerifier(verifier);
      sessionStorage.setItem('codeVerifier', verifier);
      const codeChallenge = getAuthCodeChallenge(verifier);
      const loginURL = getLoginURL(codeChallenge, appConfig);
      window.location.href = loginURL
    } else {
      const storedVerifier = sessionStorage.getItem('codeVerifier');
      if (storedVerifier) {
        dispatch(
          fetchToken<IGetAuthTokenRequestArgs>({ type: EnumTokenType.Auth, code, appConfig, codeVerifier: storedVerifier })
        );
        sessionStorage.removeItem('codeVerifier');
      } else {
        console.error('No code verifier found in sessionStorage');
      }
    }
  };

  const logout = (authtoken: ITokenState | null) => {
    if (authtoken) {
      const logoutURL = getLogoutURL(appConfig);
      window.location.href = logoutURL;
    }
  };

  return <AuthContext.Provider value={{ isAuthenticated, login, logout }}>{children}</AuthContext.Provider>;
};

export const useAuth = () => useContext(AuthContext);
